import { a3Copy } from "@/components/assignments/a3-copy";
/**
 * One piece of class work on the class tile, for the people who manage the
 * class's coursework: its title opens the record, one line of facts, a second
 * only when something needs the teacher, and its own acts. The row is its own
 * editor: Edit opens its facts beneath it, and nothing leaves the class.
 */
import { useId, useState } from "react";
import { ClipboardList, Pencil } from "lucide-react";
import { Link } from "react-router";

import { DRAFT_PLAN_SENTENCE } from "@/components/courses/materials/material-facts";
import { Button } from "@/components/ui/button";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { ListRow, ListRowMenu } from "@/components/ui/list-row";
import type { TeachingSessionAssignment } from "@/lib/api/sessionPortal";

import { dueText, pointsOf, type WorkRecord } from "./work-facts";
import { useWorkSave } from "./use-work-save";
import { WorkFacts, WorkNoteLine, WorkSaveStatus } from "./WorkFacts";

/** A row title that opens elsewhere keeps a 44px box inside the row's line. */
const ROW_LINK = "-my-3 inline-flex min-h-11 items-center py-3 text-ui font-medium text-foreground underline-offset-4 hover:underline [overflow-wrap:anywhere]";

/** A notice that is the way to its act: the same 44px box, in the notice's own ink. */
const NOTICE_LINK = "-my-3 inline-flex min-h-11 items-center py-3 font-medium text-primary underline-offset-4 hover:underline";

const LIFECYCLE_WORD: Record<WorkRecord["status"], string | null> = {
  draft: a3Copy.draft,
  published: null,
  closed: a3Copy.closed,
};

/** When work is due, in the course zone, marked up as the instant it is. */
export function WorkDue({ dueAt, timezone }: { dueAt: string | null; timezone: string }) {
  if (!dueAt) return <>{a3Copy.noDueDate}</>;
  return <>{a3Copy.due4}<time dateTime={dueAt}>{dueText(dueAt, timezone)}</time></>;
}

/**
 * A row's facts: one line of what it is, a second only when something needs
 * the teacher, and the grade item only when the work counts toward one. The
 * "n to review" notice is the way to the review (sprint decision Q3): it links
 * to the work's record with its attempts open when the viewer may review them.
 */
export function WorkRowFacts({
  assignment,
  work,
  lifecycle,
  timezone,
  reviewPath = null,
}: {
  /** The class's read: coverage, the plan's state, the grade item's name. */
  assignment: TeachingSessionAssignment;
  /** The work as last saved or read. */
  work: WorkRecord;
  lifecycle: WorkRecord["status"];
  timezone: string;
  /** Where the attempts are reviewed, when this viewer may review them. */
  reviewPath?: string | null;
}) {
  const { coverage } = assignment;
  const word = LIFECYCLE_WORD[lifecycle];
  const toReview = coverage.awaiting_review_count > 0 ? a3Copy.toReview(coverage.awaiting_review_count) : null;
  const draftPlan = lifecycle === "published" && !assignment.unit_published ? DRAFT_PLAN_SENTENCE : null;
  // The server names the grade item it read; a newer choice here waits for its next read.
  const countsToward = work.grade_item_id && work.grade_item_id === assignment.grade_item_id
    ? assignment.grade_item_label
    : null;
  return (
    <>
      <p className="text-meta text-muted-foreground">
        <WorkDue dueAt={work.due_at} timezone={timezone} />
        {work.points != null && ` · ${pointsOf(work.points)} pts`}
        {word && ` · ${word}`}
        {lifecycle !== "draft" && ` · ${coverage.submitted_count} of ${coverage.enrolled_count} submitted`}
      </p>
      {(toReview || draftPlan) && (
        <p className="text-meta text-foreground-soft">
          {toReview && (reviewPath
            ? <Link to={reviewPath} className={NOTICE_LINK}>{toReview}</Link>
            : toReview)}
          {toReview && draftPlan && " · "}
          {draftPlan}
        </p>
      )}
      {countsToward && <p className="text-meta text-muted-foreground">{a3Copy.countsToward2}{countsToward}</p>}
    </>
  );
}

export function WorkRow({
  assignment,
  recordPath,
  timezone,
  onPublish,
  justAdded = false,
}: {
  assignment: TeachingSessionAssignment;
  /** The work's record in the course, where its attempts are reviewed. */
  recordPath: string;
  timezone: string;
  /** Publishes the work and says so; resolves to the published work, or null. */
  onPublish: (assignment: TeachingSessionAssignment) => Promise<WorkRecord | null>;
  /** Just added here: its facts open with Instructions next. */
  justAdded?: boolean;
}) {
  const engine = useWorkSave(assignment, timezone);
  const [open, setOpen] = useState(justAdded);
  // Only the first opening of work just added moves focus into it.
  const [focusNext, setFocusNext] = useState(justAdded);
  const panelId = useId();
  if (engine.removed) return null;

  const { actions } = assignment;
  const work = engine.saved;
  const lifecycle = engine.lifecycle;
  const canEdit = lifecycle !== "closed" && (actions.includes("edit_terms") || actions.includes("correct_terms"));
  const canPublish = lifecycle === "draft" && actions.includes("publish_assignment");
  const canReview = actions.includes("review_attempts");
  const canClose = lifecycle === "published" && actions.includes("close_assignment");
  const canRemove = lifecycle === "draft" && actions.includes("remove_assignment");

  return (
    <ListRow
      icon={ClipboardList}
      data-record-id={assignment.id}
      detail={open && (
        <WorkFacts
          id={panelId}
          work={work}
          engine={engine}
          timezone={timezone}
          focusInstructions={focusNext}
        />
      )}
      trailing={(
        <>
          {canPublish && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={engine.busy}
              onClick={() => engine.publish(() => onPublish(assignment))}
            >{a3Copy.publish}</Button>
          )}
          {canEdit && (
            <Button
              type="button"
              size="icon-sm"
              variant="ghost"
              aria-label={a3Copy.edit(work.title)}
              aria-expanded={open}
              aria-controls={open ? panelId : undefined}
              onClick={() => {
                setFocusNext(false);
                setOpen((current) => !current);
              }}
            >
              <Pencil className="size-4" strokeWidth={1.75} aria-hidden />
            </Button>
          )}
          {/* The row's quieter acts share one menu, so the title keeps its width. */}
          {(canClose || canRemove) && (
            <ListRowMenu label={a3Copy.moreFor(work.title)}>
              {canClose && (
                <DropdownMenuItem disabled={engine.busy} onSelect={engine.close}>{a3Copy.closeToNewAttempts}</DropdownMenuItem>
              )}
              {canRemove && (
                <DropdownMenuItem disabled={engine.busy} onSelect={() => engine.remove()}>{a3Copy.remove}</DropdownMenuItem>
              )}
            </ListRowMenu>
          )}
        </>
      )}
    >
      <Link to={recordPath} className={ROW_LINK}>{work.title}</Link>
      <WorkRowFacts assignment={assignment} work={work} lifecycle={lifecycle} timezone={timezone} reviewPath={canReview ? recordPath : null} />
      {(open || engine.state) && <WorkSaveStatus engine={engine} />}
      <WorkNoteLine engine={engine} />
    </ListRow>
  );
}
